import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, RotateCcw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useIsAdminTier, useIsReadOnly, useSession } from '@/shared/auth/SessionProvider';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { canWriteCaja } from '../lib/permissions';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { cn } from '@/shared/lib/cn';
import { formatMoney, parseMoney } from '@/shared/lib/money';
import { etiquetaCaja } from '@/shared/lib/cajasEfectivo';
import { useEsCajaUsd } from '@/shared/lib/cajaMoneda';
import { FileLink } from '@/shared/components/file-upload/FileLink';
import { FileUploader } from '@/shared/components/file-upload/FileUploader';
import {
  anularMovimiento,
  editMovimiento,
  revertirCobro,
  type MovimientoRow,
} from '../api';
import { listActiveMediosDePago } from '@/shared/api/controllers/mediosDePago';

/**
 * Detail view for a single MovimientoCajas row. Read-only by default
 * with two admin actions for **manually-created** rows:
 *   - **Editar** descripción + observaciones. Monto/Categoría are locked.
 *   - **Anular** (soft-delete). Cascades to linked impuesto rows.
 *
 * Rows with `CreacionManual_MC='SI'` that aren't already Anulado and
 * don't belong to a confirmed cierre de caja can be edited/anulled.
 * All other rows (auto-generated from ventas/compras, closed in a
 * cierre) render read-only to preserve the audit trail.
 */
export function ViewMovimientoDialog({
  row,
  moneda,
  onClose,
  onMutated,
}: {
  row: MovimientoRow;
  moneda: 'ARS' | 'MXN' | 'USD';
  onClose: () => void;
  onMutated: () => void;
}) {
  const f = row.fields;
  const monto = parseMoney(f.MontoNeto_MC) ?? 0;
  const montoPesos = parseMoney(f.MontoPesos_MC);
  const tipoCambio = parseMoney(f.TipoCambio_MC);

  const isReadOnly = useIsReadOnly();
  const { profile } = useSession();
  const { config: activeCountry } = useActiveCountry();
  // Per-caja write gate (MX Nova-only cajas, Caja Diferencia, …) AND
  // baseline write permission. We pull the caja off the row itself so
  // the gate is consistent across edit / anular even when the user
  // navigates from a movimiento link rather than the page's current
  // caja selector.
  const canWrite = canWriteCaja(f.MedioPago_MC, profile?.perfil, activeCountry.code);
  const isManual = (f.CreacionManual_MC ?? '') === 'SI';
  const isAnulado = (f.Status_MC ?? '') === 'Anulado';
  const inCierre = (f.IDCierreCaja_MC ?? '').trim() !== '';
  const canMutate = isManual && !isAnulado && !inCierre && !isReadOnly && canWrite;
  // Factura/comprobante de un movimiento manual: se ve siempre; se adjunta o
  // se quita mientras el movimiento siga editable.
  const qc = useQueryClient();
  const [adjunto, setAdjunto] = useState<string | null>(
    (f.ComprobanteDriveItemID_MC ?? '').trim() ? (f.ComprobanteFileName_MC ?? 'Comprobante') : null,
  );

  // Revertir la confirmación de un cobro de venta (pedido 2026-10-02): el
  // Ingreso automático que creó "Confirmar cobro". Sólo AR (MX deprecado) y
  // admin/administración; el server revalida todo y mira los hijos.
  const isAdminTier = useIsAdminTier();
  const comprobanteId = (f.IDComprobantePago_MC ?? '').trim();
  const canRevertirCobro =
    isAdminTier &&
    activeCountry.code === 'AR' &&
    !isManual &&
    !isAnulado &&
    !inCierre &&
    (f.TipoMovimiento_MC ?? '') === 'Ingreso' &&
    (f.Categoria_MC ?? '') === 'Ventas' &&
    !(f.IDImpuesto_MC ?? '').trim() &&
    comprobanteId !== '';
  const [cajaCorrecta, setCajaCorrecta] = useState(f.MedioPago_MC ?? '');

  const [mode, setMode] = useState<
    'view' | 'edit' | 'confirmAnular' | 'cotizacion' | 'confirmRevertir'
  >('view');
  // Cajas activas de la misma moneda que la del cobro (el comprobante guarda
  // el monto en esa moneda). Misma regla que el server: "Dolar" en el nombre.
  // Comparte la query con TesoreriaCajaPage, así que ya está en caché.
  const mediosQ = useQuery({
    queryKey: ['tesoreria', activeCountry.code, 'medios'],
    queryFn: listActiveMediosDePago,
    enabled: mode === 'confirmRevertir',
    staleTime: 5 * 60_000,
  });
  const esUsd = useEsCajaUsd();
  const cajasMismaMoneda = (mediosQ.data ?? [])
    .map((m) => m.fields.Medio_MDP ?? '')
    .filter((c) => c && esUsd(c) === esUsd(f.MedioPago_MC ?? ''));
  const [descripcion, setDescripcion] = useState(f.CatDesc_MC ?? f.Descripcion_MC ?? '');
  const [observaciones, setObservaciones] = useState(f.Observaciones_MC ?? '');
  const [tipoCambioInput, setTipoCambioInput] = useState('');
  const [busy, setBusy] = useState(false);

  // La cotización sólo se puede COMPLETAR, nunca corregir: se ofrece el
  // campo únicamente si la fila es de una caja en dólares y sigue sin
  // tipo de cambio. El backend valida lo mismo.
  //
  // A diferencia del resto de la edición, esto se permite aunque el
  // movimiento esté dentro de un cierre: `computeSaldoSeries` calcula el
  // saldo con `MontoNeto_MC` (la moneda de la caja) y nunca mira
  // `MontoPesos_MC`, así que cargar la cotización no puede mover un saldo
  // ni invalidar un cierre. Sin esta excepción los 177 movimientos viejos
  // en dólares quedarían estimados para siempre: todos están cerrados.
  // `montoPesos` / `tipoCambio` ya están derivados arriba para la vista.
  const esCajaUsd = esUsd(f.MedioPago_MC);
  const faltaCotizacion =
    esCajaUsd && !(tipoCambio && tipoCambio > 0) && !(montoPesos && montoPesos > 0);

  // Origin "Ver venta/compra" link removed — both venta and compra
  // pago handlers stamp `IDComprobantePago_MC` with their pago id, so
  // there's no reliable way to disambiguate without leaking module-
  // specific knowledge into Tesorería (Categoria_MC string sniffing
  // breaks on tax rows like Ley 25.413). PowerApps never offered the
  // affordance either; the IDOperacion_MC text below already lets an
  // admin paste it into the right module manually if needed.

  const handleSaveEdit = async () => {
    if (!descripcion.trim()) {
      toast.error('La descripción es obligatoria');
      return;
    }
    setBusy(true);
    try {
      const tcNum = parseMoney(tipoCambioInput) ?? 0;
      // En modo `cotizacion` la descripción viaja igual pero sin cambios:
      // el PUT la exige y así el movimiento cerrado no se toca en nada más.
      const res = await editMovimiento(row.id, {
        descripcion: descripcion.trim(),
        observaciones: observaciones.trim() || undefined,
        ...(faltaCotizacion && tcNum > 0 ? { tipoCambio: tcNum } : {}),
      });
      if (res.outcome === 'updated') {
        toast.success(
          res.cotizacionCargada ? 'Cotización cargada' : 'Movimiento actualizado',
        );
        onMutated();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error('No se pudo guardar', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleAnular = async () => {
    setBusy(true);
    try {
      const res = await anularMovimiento(row.id);
      if (res.outcome === 'anulado') {
        toast.success(
          res.impuestosAnulados > 0
            ? `Movimiento anulado (${res.impuestosAnulados} impuestos también anulados)`
            : 'Movimiento anulado',
        );
        onMutated();
      } else if (res.outcome === 'already_anulado') {
        toast.info('El movimiento ya estaba anulado');
        onMutated();
      } else {
        toast.error(res.message);
        setMode('view');
      }
    } catch (err) {
      toast.error('No se pudo anular', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleRevertir = async () => {
    setBusy(true);
    try {
      const cambia = cajaCorrecta && cajaCorrecta !== (f.MedioPago_MC ?? '');
      const res = await revertirCobro(comprobanteId, cambia ? cajaCorrecta : undefined);
      if (res.outcome === 'revertido') {
        toast.success('Cobro revertido', {
          description: `Volvió a Cobros a Confirmar${cambia ? ` en ${etiquetaCaja(res.medioPago)}` : ''}. La venta quedó en ${res.statusRO}.`,
        });
        onMutated();
      } else {
        toast.error(res.message);
        setMode('view');
      }
    } catch (err) {
      toast.error('No se pudo revertir el cobro', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent
        className="max-w-lg"
        processing={
          busy &&
          (mode === 'confirmAnular'
            ? 'Anulando movimiento…'
            : mode === 'confirmRevertir'
              ? 'Revirtiendo cobro…'
              : 'Guardando cambios…')
        }
      >
        <DialogHeader>
          <DialogTitle>Movimiento #{row.id}</DialogTitle>
          <DialogDescription>
            {f.TipoMovimiento_MC ?? '—'} · {etiquetaCaja(f.MedioPago_MC ?? '—')}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {mode === 'confirmRevertir' ? (
            <div className="space-y-3">
              <p className="text-sm text-foreground">
                Se deshace la confirmación de este cobro de{' '}
                <strong>{f.Cliente_MC || 'la venta'}</strong>:
              </p>
              <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
                <li>Este movimiento y sus impuestos/comisiones automáticas se anulan.</li>
                <li>
                  El cobro vuelve a <strong>Cobros a Confirmar</strong> para confirmarlo de
                  nuevo. Si ya estaba facturado, la factura no se toca.
                </li>
                <li>La venta y el stock vuelven al estado anterior a la confirmación.</li>
              </ul>
              <div className="space-y-1">
                <Label htmlFor="revertir-caja">Caja correcta</Label>
                <Select value={cajaCorrecta} onValueChange={setCajaCorrecta}>
                  <SelectTrigger id="revertir-caja" aria-label="Caja correcta">
                    <SelectValue placeholder="Caja" />
                  </SelectTrigger>
                  <SelectContent>
                    {[...new Set([f.MedioPago_MC ?? '', ...cajasMismaMoneda])]
                      .filter(Boolean)
                      .map((c) => (
                        <SelectItem key={c} value={c}>
                          {etiquetaCaja(c)}
                          {c === f.MedioPago_MC && (
                            <span className="text-muted-foreground"> · la cargada</span>
                          )}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Si el cobro se cargó en la caja equivocada, elegí la correcta: al confirmarlo
                  de nuevo el movimiento se genera ahí.
                </p>
              </div>
            </div>
          ) : mode === 'confirmAnular' ? (
            <div className="space-y-3">
              <p className="text-sm text-foreground">
                ¿Seguro que querés anular este movimiento? Los impuestos
                vinculados también se anulan automáticamente.
              </p>
              <p className="text-xs text-muted-foreground">
                Esta acción cambia el estado a <strong>Anulado</strong> y el
                movimiento deja de contar en el saldo, pero queda visible en el
                historial con la etiqueta tachada.
              </p>
            </div>
          ) : mode === 'cotizacion' ? (
            <div className="space-y-3">
              <div className="space-y-1 rounded-lg border border-sky-300 bg-sky-50 p-3">
                <Label htmlFor="mov-tc-solo">Tipo de cambio</Label>
                <Input
                  id="mov-tc-solo"
                  value={tipoCambioInput}
                  onChange={(e) => setTipoCambioInput(e.target.value)}
                  placeholder="Pesos por dólar"
                  inputMode="decimal"
                />
                <p className="text-xs text-sky-900">
                  Este movimiento se cargó sin cotización. Al guardarla queda
                  fija: después no se puede volver a cambiar. No afecta el saldo
                  de la caja, el cierre, ni el Estado Financiero — ese reporte
                  convierte con la cotización de mercado de cada fecha.
                </p>
              </div>
            </div>
          ) : mode === 'edit' ? (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Sólo se puede editar descripción y observaciones
                {faltaCotizacion ? ', y completar el tipo de cambio faltante' : ''}.
                Para corregir el importe, anulá el movimiento y creá uno nuevo.
              </p>
              {faltaCotizacion && (
                <div className="space-y-1 rounded-lg border border-sky-300 bg-sky-50 p-3">
                  <Label htmlFor="mov-tc">Tipo de cambio</Label>
                  <Input
                    id="mov-tc"
                    value={tipoCambioInput}
                    onChange={(e) => setTipoCambioInput(e.target.value)}
                    placeholder="Pesos por dólar"
                    inputMode="decimal"
                  />
                  <p className="text-xs text-sky-900">
                    Este movimiento se cargó sin cotización. Al guardarla
                    queda fija: después no se puede volver a cambiar. No afecta
                    al Estado Financiero, que convierte con la cotización de
                    mercado de cada fecha.
                  </p>
                </div>
              )}
              <div className="space-y-1">
                <Label htmlFor="mov-desc">Descripción</Label>
                <Input
                  id="mov-desc"
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  maxLength={500}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="mov-obs">Observaciones</Label>
                <Textarea
                  id="mov-obs"
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  rows={3}
                  maxLength={500}
                />
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label="Fecha" value={f.Fecha_MC} />
                <Info label="Hora" value={f.Hora_MC} />
                <Info label="Categoría" value={f.Categoria_MC} />
                <Info label="Cuenta contable" value={f.CuentaContable_MC} />
                <Info
                  label="Importe"
                  value={formatMoney(monto, moneda)}
                  emphasis={
                    f.TipoMovimiento_MC === 'Ingreso' ||
                    f.TipoMovimiento_MC === 'Apertura de Caja'
                      ? 'positive'
                      : f.TipoMovimiento_MC === 'Egreso' ||
                          f.TipoMovimiento_MC === 'Cierre de Caja'
                        ? 'negative'
                        : undefined
                  }
                />
                <Info
                  label="Saldo tras movimiento"
                  value={formatMoney(row.saldoAt, moneda)}
                  emphasis={row.saldoAt < 0 ? 'negative' : undefined}
                />
                {montoPesos != null && tipoCambio != null && (
                  <>
                    <Info label="Tipo de cambio" value={f.TipoCambio_MC} />
                    <Info label="Monto (ARS)" value={formatMoney(montoPesos, 'ARS')} />
                  </>
                )}
                <Info label="Estado" value={f.Status_MC} />
                <Info label="Usuario" value={f.UsuarioApp_MC} />
                {f.Cliente_MC && <Info label="Cliente" value={f.Cliente_MC} />}
                {isManual && <Info label="Origen" value="Manual (Tesorería)" />}
                {f.IDCierreCaja_MC && (
                  <Info label="Cierre" value={f.IDCierreCaja_MC} />
                )}
              </div>

              {f.Descripcion_MC && (
                <div className="mt-3 space-y-0.5">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Descripción
                  </p>
                  <p className="text-sm text-foreground">{f.Descripcion_MC}</p>
                </div>
              )}
              {f.Observaciones_MC && (
                <div className="mt-2 space-y-0.5">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Observaciones
                  </p>
                  <p className="whitespace-pre-line text-sm text-foreground">{f.Observaciones_MC}</p>
                </div>
              )}
              {isManual && (adjunto || canMutate) && (
                <div className="mt-3 space-y-1">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Factura o comprobante
                  </p>
                  {adjunto ? (
                    <FileLink
                      entityType="ComprobanteTesoreria"
                      entityId={row.id}
                      fileName={adjunto}
                      allowDelete={canMutate}
                      onDeleted={() => {
                        setAdjunto(null);
                        qc.invalidateQueries({ queryKey: ['tesoreria'] });
                      }}
                    />
                  ) : (
                    <FileUploader
                      entityType="ComprobanteTesoreria"
                      entityId={row.id}
                      compact
                      onUploaded={(r) => {
                        setAdjunto(r.fileName);
                        qc.invalidateQueries({ queryKey: ['tesoreria'] });
                      }}
                    />
                  )}
                </div>
              )}

            </>
          )}
        </DialogBody>
        <DialogFooter>
          {mode === 'confirmRevertir' ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => setMode('view')}
                disabled={busy}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleRevertir}
                disabled={busy || !cajaCorrecta}
                className="gap-2 bg-amber-600 text-white hover:bg-amber-700"
              >
                <RotateCcw className="h-4 w-4" aria-hidden />
                Revertir cobro
              </Button>
            </>
          ) : mode === 'confirmAnular' ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => setMode('view')}
                disabled={busy}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleAnular}
                disabled={busy}
                className="gap-2 bg-rose-600 text-white hover:bg-rose-700"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
                Confirmar anulación
              </Button>
            </>
          ) : mode === 'edit' || mode === 'cotizacion' ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => setMode('view')}
                disabled={busy}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleSaveEdit}
                disabled={
                  busy ||
                  (mode === 'cotizacion' && (parseMoney(tipoCambioInput) ?? 0) <= 0)
                }
              >
                Guardar
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={onClose}>
                Cerrar
              </Button>
              {/* Cargar la cotización faltante se ofrece aunque el movimiento
                  esté cerrado: no toca el saldo (ver nota arriba). Sólo cuando
                  Editar no está disponible, para no duplicar el acceso. */}
              {!canMutate && faltaCotizacion && !isAnulado && !isReadOnly && canWrite && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setMode('cotizacion')}
                  className="gap-2 border-sky-300 text-sky-800 hover:bg-sky-50"
                >
                  <Pencil className="h-4 w-4" aria-hidden />
                  Cargar cotización
                </Button>
              )}
              {canRevertirCobro && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setMode('confirmRevertir')}
                  className="gap-2 border-amber-300 text-amber-800 hover:bg-amber-50"
                >
                  <RotateCcw className="h-4 w-4" aria-hidden />
                  Revertir cobro
                </Button>
              )}
              {canMutate && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setMode('edit')}
                    className="gap-2"
                  >
                    <Pencil className="h-4 w-4" aria-hidden />
                    Editar
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setMode('confirmAnular')}
                    className="gap-2 border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                    Anular
                  </Button>
                </>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Info({
  label,
  value,
  emphasis,
}: {
  label: string;
  value?: string | null;
  emphasis?: 'positive' | 'negative';
}) {
  return (
    <div className="space-y-0.5">
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p
        className={cn(
          'text-sm',
          emphasis === 'positive'
            ? 'font-semibold text-emerald-700'
            : emphasis === 'negative'
              ? 'font-semibold text-rose-700'
              : 'text-foreground',
        )}
      >
        {value && value.trim() ? value : '—'}
      </p>
    </div>
  );
}
